// Extracted from original/src/components/CashTransfers.tsx (2,643 lines; ADR-08 §2.1 "copy, then cut logic"), the screen
// frame only — the form, the details window and the warning dialogs are in TransferFormModal.tsx, TransferReceiptModal.tsx
// and TransferDialogs.tsx:
//   hub banner + register button + balance cards   :914-997
//   vault / office sub-tabs                        :999-1015
//   search & filters panel + share button          :1017-1105
//   registry list (header, table, row actions)     :1107-1253
// Kept: every class string, icon and size, the emoji of the old labels and flags (`availableCurrencies` :88-116), the
// list columns (the partner-fee column on the office tab only), the struck-through cancelled rows, the
// `#{id}` / code cell, the beneficiary cell with the partner and customer lines, the settlement badge in the total cell.
// Cut: `wallets.find(w => w.id === 1 | 11)` / `currenciesBalances` (stored balances, :294-308) → `bannerCards` from the
// ledger (D-013); `formatRemittanceNumber` / `getCurrencySymbol` / `getCommissionCurrencySymbol` (:121-345) →
// preformatted labels; `filteredTransfers` (:502-519) → `rows` (the container filters); `debts.find(...)` by numeric id
// (:1164, :1169) → names carried by the row (D-088, accounts by id); `currentUser?.role === 'admin' ||
// hasPermission(…'delete_movements')` (:1226) → `canCancel` (D-034 token); `handleSecurityCheck` + `onDeleteTransfer`
// (:1228-1236) → `onCancel` (the container asks the reason and the PIN, D-086); `handleShareClick` (:521-615) →
// `onShare`; `showExchangeRate` (per-browser) → the office's "hide USD dealing" (D-066: the container leaves the USD
// card out).
// Changed by decision: the row's trash button cancels (reversal entry) and is absent on a cancelled row — there is no
// permanent delete (D-086; old second click "حذف نهائي" removed the record); a transfer held for second-person
// approval shows "⏳ بانتظار الاعتماد", a rejected / withdrawn one is struck through like a cancelled one (D-035,
// V-34); the code cell shows the server code (D-087) and the "#" the office's running number (old array id).
// Added: data-testid attributes.
import { Building2, Calculator, FileText, Plus, Search, SendHorizontal, Share2, Trash2, Wallet } from 'lucide-react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { useI18n } from '../i18n-context.js';

export type CtTab = 'vault' | 'office';
export type CtDirection = 'sent' | 'received';
export type CtPayment = 'cash' | 'credit' | 'trust';
export type CtStatus = 'posted' | 'pending' | 'rejected' | 'withdrawn' | 'cancelled';

/** One balance card of the hub banner (old :954-995). */
export interface CtBannerCard {
  readonly code: string;
  readonly flag: string;
  readonly label: string;
  /** "3,000,000.00 د.ع" (old `formatRemittanceNumber(balance)` + suffix). */
  readonly amountLabel: string;
  readonly tone: 'iqd' | 'usd' | 'other';
}

export interface CtRow {
  readonly id: string;
  /** "#12" — the office's running number. */
  readonly numberLabel: string;
  readonly code: string;
  readonly status: CtStatus;
  readonly beneficiaryName: string;
  readonly destination: string;
  readonly partnerName: string | null;
  /** The customer account on credit / trust (D-084), with the old line label. */
  readonly customerName: string | null;
  readonly direction: CtDirection;
  readonly payment: CtPayment;
  readonly amountLabel: string;
  readonly commissionLabel: string;
  readonly partnerFeeLabel: string;
  readonly totalLabel: string;
  /** "1,656,400.00 د.ع" — the settlement cash of a foreign-currency transfer (D-085 (a)), else null. */
  readonly settlementLabel: string | null;
}

export interface CashTransfersProps {
  readonly tab: CtTab;
  readonly onTabChange: (tab: CtTab) => void;
  readonly bannerCards: readonly CtBannerCard[];
  readonly rows: readonly CtRow[];
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly typeFilter: 'all' | CtDirection;
  readonly onTypeFilterChange: (v: 'all' | CtDirection) => void;
  readonly currencyFilter: string;
  readonly onCurrencyFilterChange: (v: string) => void;
  /** Currency options after "all" (flag + name, symbol). */
  readonly currencyOptions: readonly SelectOption[];
  readonly paymentFilter: 'all' | CtPayment;
  readonly onPaymentFilterChange: (v: 'all' | CtPayment) => void;
  readonly onShare: () => void;
  readonly onAdd: () => void;
  readonly onDetails: (id: string) => void;
  readonly canCancel: boolean;
  readonly onCancel: (id: string) => void;
}

export function CashTransfers(props: CashTransfersProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const activeSubTab = props.tab;
  const filteredTransfers = props.rows;
  const struck = (s: CtStatus) => s === 'cancelled' || s === 'rejected' || s === 'withdrawn';

  return (
    <div className="space-y-6" id="fin-transfers-container" data-testid="transfers-screen">
      {/* Dynamic Professional Hub Banner */}
      <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden border border-indigo-500/10 shadow-xl shadow-indigo-950/20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.08),transparent)] pointer-events-none" />

        <div className="flex flex-wrap justify-between items-center gap-6 relative z-10">
          <div className={`space-y-3 ${lang === 'ar' ? 'text-right' : 'text-left'}`}>
            <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 border border-indigo-400/20 rounded-full text-[10px] font-black uppercase tracking-wider">
              {t('ct.banner_badge')}
            </span>
            <div className="flex items-center gap-3">
              <div className="p-3.5 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl shadow-lg shadow-indigo-500/25 text-white">
                <SendHorizontal size={26} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black tracking-tight">{t('ct.title')}</h1>
                <p className="text-gray-400 mt-1 text-xs font-semibold max-w-xl leading-relaxed">
                  {t('ct.main_desc')}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={props.onAdd}
            className="bg-white hover:bg-indigo-50 text-indigo-950 font-black px-6 py-4 rounded-2xl active:scale-95 transition-all text-xs flex items-center gap-2 shadow-lg shadow-white/5"
            id="register-transfer-btn"
            data-testid="transfers-add"
          >
            <Plus size={16} className="text-indigo-600" />
            {t('ct.add_btn')}
          </button>
        </div>

        {/* Real-time Tills Indicators inside the banner */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8 pt-6 border-t border-white/5">
          {props.bannerCards.map((c) =>
            c.tone === 'iqd' ? (
              <div key={c.code} className="bg-white/5 border border-white/5 rounded-2xl p-3.5 flex items-center gap-3" data-testid={`transfers-card-${c.code}`}>
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-black text-sm">
                  {c.flag}
                </div>
                <div className={`${lang === 'ar' ? 'text-right' : 'text-left'}`}>
                  <span className="text-[10px] text-gray-400 block font-bold">{c.label}</span>
                  <span className="font-mono text-sm font-black text-emerald-400">
                    {c.amountLabel}
                  </span>
                </div>
              </div>
            ) : c.tone === 'usd' ? (
              <div key={c.code} className="bg-white/5 border border-white/5 rounded-2xl p-3.5 flex items-center gap-3" data-testid={`transfers-card-${c.code}`}>
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center font-black text-sm">
                  {c.flag}
                </div>
                <div className={`${lang === 'ar' ? 'text-right' : 'text-left'}`}>
                  <span className="text-[10px] text-gray-400 block font-bold">{c.label}</span>
                  <span className="font-mono text-sm font-black text-amber-400">
                    {c.amountLabel}
                  </span>
                </div>
              </div>
            ) : (
              <div key={c.code} className="bg-white/5 border border-white/5 rounded-2xl p-3.5 flex items-center gap-3" data-testid={`transfers-card-${c.code}`}>
                <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-black text-sm">
                  {c.flag}
                </div>
                <div className={`${lang === 'ar' ? 'text-right' : 'text-left'}`}>
                  <span className="text-[10px] text-gray-400 block font-bold truncate max-w-[110px]">{c.label}</span>
                  <span className="font-mono text-sm font-black text-indigo-300">
                    {c.amountLabel}
                  </span>
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      {/* Sub-Tabs Segregation Control: Vault vs Office */}
      <div className="flex bg-gray-100 dark:bg-[#161b22] p-1.5 rounded-2xl max-w-sm" id="sub-transfers-tabs">
        <button
          onClick={() => props.onTabChange('vault')}
          className={`flex-1 py-3 text-xs font-black rounded-xl transition-all flex items-center justify-center gap-2 ${activeSubTab === 'vault' ? 'bg-white dark:bg-[#0d1117] text-indigo-600 dark:text-indigo-400 shadow' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="transfers-tab-vault"
        >
          <Wallet size={14} />
          {t('ct.tab_vault')}
        </button>
        <button
          onClick={() => props.onTabChange('office')}
          className={`flex-1 py-3 text-xs font-black rounded-xl transition-all flex items-center justify-center gap-2 ${activeSubTab === 'office' ? 'bg-white dark:bg-[#0d1117] text-indigo-600 dark:text-indigo-400 shadow' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="transfers-tab-office"
        >
          <Building2 size={14} />
          {t('ct.tab_office')}
        </button>
      </div>

      {/* Search & filters panel */}
      <div className="bg-white dark:bg-[#0d1117] rounded-3xl p-6 border border-slate-100 dark:border-[#30363d] shadow-sm space-y-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className={`space-y-0.5 ${lang === 'ar' ? 'text-right' : 'text-left'}`}>
            <h3 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider">{t('ct.quick_filters')}</h3>
            <p className="text-[10px] text-gray-400 font-medium">{t('ct.quick_filters_desc')}</p>
          </div>
          <button
            onClick={props.onShare}
            disabled={filteredTransfers.length === 0}
            className={`px-4 py-2 text-xs font-extrabold rounded-xl transition-all flex items-center gap-2 active:scale-95 shadow-sm border ${
              filteredTransfers.length === 0
                ? 'bg-gray-100 dark:bg-[#161b22] text-gray-400 border-gray-200 dark:border-[#30363d] cursor-not-allowed opacity-50'
                : 'bg-[#fafbfc] dark:bg-[#21262d] text-slate-800 dark:text-slate-200 border-slate-200 dark:border-[#30363d] hover:bg-slate-50 dark:hover:bg-[#30363d]'
            }`}
            data-testid="transfers-share"
          >
            <Share2 size={13} className="text-indigo-500" />
            {t('ct.share_btn')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Custom Search Box */}
          <div className="relative md:col-span-1">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={props.search || ''}
              onChange={(e) => props.onSearchChange(e.target.value)}
              placeholder={t('ct.search_placeholder')}
              className="w-full pl-4 pr-11 py-3 bg-gray-50 dark:bg-[#05070a] border border-gray-150 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-black text-slate-700 dark:text-slate-200"
              data-testid="transfers-search"
            />
          </div>

          {/* Direction filter */}
          <div className="min-w-[160px]">
            <CustomSelect
              options={[
                { id: 'all', label: t('ct.filter_dir_all') },
                { id: 'sent', label: t('ct.filter_dir_sent') },
                { id: 'received', label: t('ct.filter_dir_received') },
              ]}
              value={props.typeFilter}
              onChange={(val) => props.onTypeFilterChange(val as 'all' | CtDirection)}
              headerTitle={t('ct.filter_dir_header')}
              searchable={false}
              size="md"
            />
          </div>

          {/* Currency filter */}
          <div className="min-w-[200px] flex-1">
            <CustomSelect
              options={[{ id: 'all', label: t('ct.filter_cur_all') }, ...props.currencyOptions]}
              value={props.currencyFilter}
              onChange={(val) => props.onCurrencyFilterChange(val)}
              headerTitle={t('ct.filter_cur_header')}
              searchable={true}
              size="md"
            />
          </div>

          {/* Payment category filter */}
          <div className="min-w-[190px]">
            <CustomSelect
              options={[
                { id: 'all', label: t('ct.filter_pay_all') },
                { id: 'cash', label: t('ct.filter_pay_cash') },
                { id: 'credit', label: t('ct.filter_pay_credit') },
                { id: 'trust', label: t('ct.filter_pay_trust') },
              ]}
              value={props.paymentFilter}
              onChange={(val) => props.onPaymentFilterChange(val as 'all' | CtPayment)}
              headerTitle={t('ct.filter_pay_header')}
              searchable={false}
              size="md"
            />
          </div>
        </div>
      </div>

      {/* Structured List in alignment with selected Sub-Tab */}
      <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-slate-100 dark:border-[#30363d] overflow-hidden shadow-sm">
        <div className="p-6 border-b border-dashed border-slate-100 dark:border-[#30363d] bg-slate-50/50 dark:bg-[#161b22]/10 flex justify-between items-center text-xs">
          <span className="font-extrabold text-slate-500 dark:text-[#8b949e]">
            {activeSubTab === 'vault' ? t('ct.registry_vault') : t('ct.registry_office')}
          </span>
          <span className="font-mono bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-3 py-1 rounded-full font-black" data-testid="transfers-count">
            {filteredTransfers.length} {t('ct.entries_suffix')}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-right border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/20 dark:bg-[#161b22]/30 text-slate-500 dark:text-[#8b949e] border-b border-slate-100 dark:border-[#30363d] font-black uppercase text-[10px] tracking-wider">
                <th className="p-4">{t('ct.col_details')}</th>
                <th className="p-4">{t('ct.col_beneficiary')}</th>
                <th className="p-4">{t('ct.col_type')}</th>
                <th className="p-4 text-center">{t('ct.col_principal')}</th>
                <th className="p-4 text-center">{t('ct.col_commission')}</th>
                {activeSubTab === 'office' && <th className="p-4 text-center">{t('ct.col_partner_fee')}</th>}
                <th className="p-4 text-center bg-indigo-50/10 dark:bg-indigo-950/5 font-black text-indigo-700 dark:text-indigo-400">{t('ct.col_total')}</th>
                <th className="p-4 text-center">{t('ct.col_options')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-[#30363d] font-sans">
              {filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan={activeSubTab === 'office' ? 8 : 7} className="p-16 text-center text-gray-400 font-bold">
                    <div className="flex flex-col items-center gap-3">
                      <Calculator size={32} className="text-gray-300" />
                      <span>{t('ct.no_transfers')}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTransfers.map((item) => {
                  const isCancelled = struck(item.status);
                  return (
                    <tr key={item.id} className={`transition-colors ${isCancelled ? 'bg-red-50/20 dark:bg-red-950/5 opacity-60' : 'hover:bg-slate-50/50 dark:hover:bg-[#161b22]/10'}`} data-testid={`transfer-row-${item.id}`}>
                      <td className={`p-4 font-mono font-black ${isCancelled ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                        <span className="text-gray-400 font-normal">{item.numberLabel}</span>
                        <div className={`text-[10px] flex items-center gap-1 ${isCancelled ? 'text-rose-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${isCancelled ? 'bg-rose-400' : 'bg-indigo-500 animate-pulse'}`} />
                          {item.code}
                        </div>
                      </td>

                      <td className={`p-4 ${isCancelled ? 'line-through text-slate-400' : ''}`}>
                        <div className={`font-extrabold ${isCancelled ? 'text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>{item.beneficiaryName}</div>
                        <div className="text-[10px] font-mono text-slate-400">📍 {item.destination}</div>

                        {/* Dynamic references */}
                        {item.partnerName !== null && (
                          <div className={`text-[10px] font-extrabold mt-1 ${isCancelled ? 'text-slate-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            🤝 {t('ct.partner_office_label')} {item.partnerName}
                          </div>
                        )}
                        {item.customerName !== null && (
                          <div className={`text-[10px] font-extrabold mt-0.5 ${isCancelled ? 'text-slate-400' : (item.payment === 'credit' ? 'text-rose-600 dark:text-rose-400' : 'text-sky-600 dark:text-sky-400')}`}>
                            {item.payment === 'credit' ? '📈' : '📉'} {item.payment === 'credit' ? t('ct.on_debt_of') : t('ct.deducted_trust_of')} {item.customerName || t('ct.unknown_ledger')}
                          </div>
                        )}
                      </td>

                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {item.status === 'cancelled' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-black text-[9px] w-fit bg-rose-100 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400">
                              ✖ {t('ct.cancelled')}
                            </span>
                          ) : item.status === 'rejected' || item.status === 'withdrawn' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-black text-[9px] w-fit bg-rose-100 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400">
                              ✖ {t(item.status === 'rejected' ? 'ct.status_rejected' : 'ct.status_withdrawn')}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-black text-[9px] w-fit ${item.direction === 'sent' ? 'bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400' : 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400'}`}>
                              {item.direction === 'sent' ? t('ct.sent') : t('ct.received')}
                            </span>
                          )}
                          <span className={`text-[10px]/normal font-extrabold ${isCancelled ? 'text-slate-400 line-through' : (item.payment === 'cash' ? 'text-gray-500' : (item.payment === 'credit' ? 'text-rose-500' : 'text-blue-500'))}`}>
                            {item.payment === 'cash' ? t('ct.pay_cash') : (item.payment === 'credit' ? t('ct.pay_credit') : t('ct.pay_trust'))}
                          </span>
                          {item.status === 'pending' && (
                            <span className="text-[10px]/normal font-extrabold text-amber-600 dark:text-amber-400" data-testid="transfer-pending">
                              {t('ct.status_pending')}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={`p-4 text-center font-mono font-black ${isCancelled ? 'line-through text-slate-400' : ''}`}>
                        {item.amountLabel}
                      </td>

                      <td className={`p-4 text-center font-mono font-black ${isCancelled ? 'line-through text-slate-400 opacity-60' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        +{item.commissionLabel}
                      </td>

                      {activeSubTab === 'office' && (
                        <td className={`p-4 text-center font-mono font-black ${isCancelled ? 'line-through text-slate-400 opacity-60' : 'text-amber-600 dark:text-amber-400'}`}>
                          +{item.partnerFeeLabel}
                        </td>
                      )}

                      <td className={`p-4 text-center font-mono font-black ${isCancelled ? 'line-through text-slate-400 bg-slate-100/50' : 'text-indigo-700 dark:text-indigo-400 bg-indigo-50/10 dark:bg-indigo-950/5'}`}>
                        <div>{item.totalLabel}</div>
                        {item.settlementLabel !== null && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                              🏦 {item.settlementLabel}
                            </span>
                          </div>
                        )}
                      </td>

                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => props.onDetails(item.id)}
                            className="p-2.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 hover:bg-indigo-100 rounded-xl transition-all"
                            title={t('ct.details_title')}
                            data-testid={`transfer-details-${item.id}`}
                          >
                            <FileText size={14} />
                          </button>

                          {props.canCancel && item.status === 'posted' && (
                            <button
                              onClick={() => props.onCancel(item.id)}
                              className="p-2.5 text-rose-600 bg-rose-50 dark:bg-rose-950 hover:bg-rose-100 rounded-xl transition-all cursor-pointer"
                              title={t('ct.cancel_title')}
                              data-testid={`transfer-cancel-${item.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
